import type { NavCandidate, NavConfirmation } from "~/helpers/aiChat/fernieActions";

import { Box, ButtonBase, Icon, Typography } from "@mui/material";

import FernieCardHeader from "~/components/FernieCardHeader";
import FernieCardShell from "~/components/FernieCardShell";
import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import { buildConfirmationPrompt } from "~/helpers/aiChat/fernieFeedback";

interface FernieNavConfirmCardProps {
	chosenPath?: string;
	confirmation: NavConfirmation;
	onChoose: (candidate: NavCandidate) => void;
}

const CandidateButton = ({
	candidate,
	chosen,
	onChoose,
	resolved
}: {
	candidate: NavCandidate;
	chosen: boolean;
	onChoose: (candidate: NavCandidate) => void;
	resolved: boolean;
}) => (
	<ButtonBase
		disabled={resolved}
		onClick={() => onChoose(candidate)}
		sx={{
			...fernieButtonSx.focusRing,
			"&:hover": { backgroundColor: resolved ? undefined : "primary.dark" },
			alignItems: "center",
			backgroundColor: chosen || !resolved ? "primary.main" : "action.disabledBackground",
			borderRadius: "8px",
			color: chosen || !resolved ? "primary.contrastText" : "text.disabled",
			cursor: resolved ? "default" : "pointer",
			display: "flex",
			font: "inherit",
			fontSize: "0.78rem",
			fontWeight: 600,
			gap: 0.5,
			justifyContent: "center",
			opacity: resolved && !chosen ? 0.5 : 1,
			px: 1.5,
			py: 0.75,
			transition: "background-color 0.15s"
		}}
	>
		<Icon sx={{ fontSize: 16 }}>{chosen ? "check" : "arrow_forward"}</Icon>
		{candidate.label}
	</ButtonBase>
);

const FernieNavConfirmCard = ({ chosenPath, confirmation, onChoose }: FernieNavConfirmCardProps) => {
	const resolved = chosenPath !== undefined;
	const single = confirmation.candidates.length === 1;
	const prompt = buildConfirmationPrompt([confirmation]) ?? "";

	return (
		<FernieCardShell accent={resolved ? "success" : "primary"} tinted={resolved}>
			<FernieCardHeader
				color={resolved ? "success.main" : "primary.main"}
				icon={resolved ? "check_circle" : "near_me"}
				mono={false}
				toolName="Navigation"
			/>
			<Typography sx={{ color: "text.primary", fontSize: "0.82rem", lineHeight: 1.5, px: 1.5, py: 1 }}>
				{prompt}
			</Typography>
			<Box
				sx={{
					borderColor: "divider",
					borderTop: "1px solid",
					display: "flex",
					flexDirection: single ? "row" : "column",
					gap: 1,
					px: 1.5,
					py: 1
				}}
			>
				{confirmation.candidates.map((candidate) => (
					<CandidateButton
						candidate={candidate}
						chosen={chosenPath === candidate.path}
						key={candidate.path}
						onChoose={onChoose}
						resolved={resolved}
					/>
				))}
			</Box>
		</FernieCardShell>
	);
};

export default FernieNavConfirmCard;
